import { isEnter } from "../utils/keyboardEvents";
import { parseDateInput, parseTimeInput, toDate, toLocalDateISOString } from "../utils/Dates";
import { Fragment, useContext } from "react";
import { TextInput, TextInputProps } from "./TextInput";
import { observer } from "mobx-react";
import { DatePicker } from "./DatePicker";
import { flip, offset, useFloating } from "@floating-ui/react-dom";
import { FloatingOverlay } from "./FloatingOverlay";
import { classNames } from "../utils/classNames";
import { IconButton } from "./Button";
import { Popover } from "@headlessui/react";
import { when } from "../utils/functions";

import {
  DCChangeEventHandler,
  useChangeEventHandler,
  useDispatch,
  useFocusEventHandler,
  useKeyboardEventHandler,
  useTranslation,
} from "../stores/RootStoreContext";
import { LocalDateFieldModel, TimeFieldModel } from "../common/commonForms";
import { EventDispatcher } from "../events/eventsDomain";
import { TableContext } from "./TableContext";

export type DateInputProps = Omit<TextInputProps, "type" | "onChange"> & {
  onInput: DCChangeEventHandler<HTMLInputElement>;
  onChange: (
    params: { parsed: Date | null; input: string; error: boolean },
    dispatch: EventDispatcher,
  ) => void;
};

export function DateInput({ placeholder, onChange, ...props }: DateInputProps) {
  const { t } = useTranslation();
  const tableContext = useContext(TableContext);
  const dispatch = useDispatch();
  const blurHandler = useFocusEventHandler(props.onBlur);
  const inputHandler = useChangeEventHandler(props.onInput);
  const keyDownHandler = useKeyboardEventHandler(props.onKeyDown);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-end",
    middleware: [flip(), offset(3)],
  });

  return (
    <Popover as={Fragment}>
      {({ open, close }) => (
        <>
          <TextInput
            {...props}
            ref={refs.setReference}
            type={"text"}
            placeholder={placeholder ?? t("date-picker.placeholder")}
            onKeyDown={(event, dispatch) => {
              if (isEnter(event)) {
                onChange(parseDateInput(event.currentTarget.value), dispatch);
              } else {
                keyDownHandler?.(event);
              }
            }}
            onChange={inputHandler}
            onBlur={(event, dispatch) => {
              onChange(parseDateInput(String(props.value ?? "")), dispatch);
              if (!event.target.parentElement?.contains(event.relatedTarget)) {
                blurHandler?.(event);
              }
            }}
            message={
              props.message ?? (props.error ? t("date-picker.invalid-date-message") : undefined)
            }
            addon={
              props.disabled || props.readOnly ? undefined : (
                <Popover.Button as={Fragment}>
                  <IconButton
                    color={"gray"}
                    icon={"calendar"}
                    tooltip={t("show-datepicker")}
                    compact={tableContext?.compact}
                    onBlur={(event) => {
                      if (!open && !event.relatedTarget?.parentElement?.contains(event.target)) {
                        blurHandler?.(event);
                      }
                    }}
                  />
                </Popover.Button>
              )
            }
          />
          {open && (
            <FloatingOverlay layer={"popover"} modal onClick={close}>
              <Popover.Panel
                ref={refs.setFloating}
                className={classNames(
                  "tw-rounded",
                  "tw-border",
                  "border-gray",
                  "tw-shadow",
                  "bg-default",
                  "tw-overflow-auto",
                )}
                style={floatingStyles}
                static
              >
                <DatePicker
                  input={String(props.value)}
                  onSelect={(d) => {
                    close();
                    onChange(
                      { input: toLocalDateISOString(d), parsed: toDate(d), error: false },
                      dispatch,
                    );
                  }}
                />
              </Popover.Panel>
            </FloatingOverlay>
          )}
        </>
      )}
    </Popover>
  );
}

export type MDateInputProps = {
  model: LocalDateFieldModel;
} & Partial<Omit<DateInputProps, "value">>;

export const MDateInput = observer(function MDateInput({
  model,
  onInput,
  onChange,
  onBlur,
  ...props
}: MDateInputProps) {
  const { t } = useTranslation();
  return (
    <DateInput
      {...props}
      value={model.inputValue}
      onInput={onInput ?? model.setInputValue}
      onChange={(params, dispatch) => {
        if (onChange) {
          onChange(params, dispatch);
        } else {
          model.setAndCommitInputValue(params.input);
        }
      }}
      onBlur={onBlur}
      error={Boolean(model.error)}
      message={when(model.error, t)}
    />
  );
});

export type TimeInputProps = Omit<TextInputProps, "type" | "onChange"> & {
  onInput: DCChangeEventHandler<HTMLInputElement>;
  onChange: (
    params: { parsed: string | null; input: string; error: boolean },
    dispatch: EventDispatcher,
  ) => void;
};

export function TimeInput({ placeholder, onChange, ...props }: TimeInputProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const blurHandler = useFocusEventHandler(props.onBlur);
  const keyDownHandler = useKeyboardEventHandler(props.onKeyDown);
  const inputHandler = useChangeEventHandler(props.onInput);

  return (
    <TextInput
      {...props}
      type={"text"}
      placeholder={placeholder ?? t("time-picker.placeholder")}
      onKeyDown={(event) => {
        if (isEnter(event)) {
          onChange(parseTimeInput(event.currentTarget.value), dispatch);
        } else {
          keyDownHandler?.(event);
        }
      }}
      onChange={inputHandler}
      onBlur={(event) => {
        onChange(parseTimeInput(String(props.value ?? "")), dispatch);
        blurHandler?.(event);
      }}
      message={props.message ?? (props.error ? t("date-picker.invalid-time-message") : undefined)}
      icon={"clock"}
    />
  );
}

export type MTimeInputProps = {
  model: TimeFieldModel;
} & Partial<Omit<TimeInputProps, "value">>;

export const MTimeInput = observer(function MTimeInput({
  model,
  label,
  placeholder,
  onInput,
  onChange,
  onBlur,
  ...props
}: MTimeInputProps) {
  const { t } = useTranslation();
  return (
    <TimeInput
      {...props}
      label={label}
      placeholder={placeholder}
      value={model.inputValue}
      onInput={onInput ?? model.setInputValue}
      onChange={onChange ?? model.commit}
      onBlur={onBlur}
      error={Boolean(model.error)}
      message={when(model.error, t)}
    />
  );
});
